import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  IconButton,
  Collapse
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  TuneRounded as TuneIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  LocalOffer as LocalOfferIcon,
  CollectionsBookmark as CollectionsBookmarkIcon
} from '@mui/icons-material';
import SearchInput from '../../atoms/SearchInput/SearchInput';
import SortSelector from '../../molecules/SortSelector/SortSelector';
import {
  getOperationalMode,
  operationalComponentSurfaces,
  operationalElevation,
  operationalRadii,
  operationalSizing,
  operationalSpacing
} from '../../../design-system/tokens';

/**
 * PageToolbar - Organismo reutilizável para toolbar de páginas
 *
 * CLASSIFICAÇÃO: ORGANISMO
 *
 * Compõe átomos (SearchInput, ViewToggle) e moléculas (SortSelector)
 * em uma toolbar consistente para todas as páginas.
 *
 * @see https://bradfrost.com/blog/post/atomic-web-design/
 */
const PageToolbar = ({
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Buscar...',
  sortValue,
  onSortChange,
  sortOptions,
  viewValue,
  onViewChange,
  viewOptions = [
    { value: 'grid', icon: ViewModuleIcon, tooltip: 'Visualização em grade' },
    { value: 'list', icon: ViewListIcon, tooltip: 'Visualização em lista' }
  ],
  headerActions,
  extraControls,
  resultCount,
  resultLabel = 'resultado(s) encontrado(s)',
  fixed = false,
  className = '',
  // Nova prop para ação de criar novo item (extraída de headerActions para mobile)
  onNewItem,
  newItemLabel,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileToolbarExpanded, setMobileToolbarExpanded] = useState(false);
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];

  // Detectar mudanças no estado do sidebar (para modo fixed)
  useEffect(() => {
    if (!fixed) return;

    const checkSidebarState = () => {
      const layoutElement = document.querySelector('.layout-with-open-sidebar, .layout-with-closed-sidebar');
      if (layoutElement) {
        setSidebarOpen(layoutElement.classList.contains('layout-with-open-sidebar'));
      }
    };

    checkSidebarState();

    const observer = new MutationObserver(checkSidebarState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });

    return () => observer.disconnect();
  }, [fixed]);

  // No mobile, filtrar viewOptions para mostrar apenas 'grid' (list não funciona bem em telas estreitas)
  const filteredViewOptions = isMobile
    ? viewOptions.filter(opt => opt.value === 'grid')
    : viewOptions;

  // Helper para obter o icone correto
  const getPageIcon = (iconSize = 32) => {
    if (title.includes('Tags')) return <LocalOfferIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: iconSize }} />;
    if (title.includes('Coleções')) return <CollectionsBookmarkIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: iconSize }} />;
    return null;
  };

  // Renderização do conteúdo completo da toolbar (usado tanto em desktop quanto em mobile expandido)
  const renderToolbarContent = () => (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      {/* Título - apenas no desktop */}
      {!isMobile && (
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mr: 1, display: 'flex', alignItems: 'center' }}>
          {getPageIcon(32)}
          {title}
        </Typography>
      )}

      {/* Busca */}
      {onSearchChange && (
        <SearchInput
          value={searchValue}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
          sx={{ flexGrow: 1, minWidth: isMobile ? '100%' : '200px' }}
        />
      )}

      {/* Controles extras (ex: select all, bulk actions) - antes da ordenação */}
      {extraControls}

      {/* Ordenação */}
      {onSortChange && sortOptions && (
        <SortSelector
          value={sortValue}
          onChange={onSortChange}
          options={sortOptions}
        />
      )}

      {/* Toggle de visualização - no mobile, só mostra se tiver mais de uma opção */}
      {onViewChange && filteredViewOptions.length > 1 && (
        <ToggleButtonGroup
          value={viewValue}
          exclusive
          onChange={onViewChange}
          aria-label="visualização"
          size="small"
          sx={{
            height: operationalSizing.compact,
            borderRadius: `${operationalRadii.control}px`
          }}
        >
          {filteredViewOptions.map((option) => {
            const Icon = option.icon;
            return (
              <ToggleButton
                key={option.value}
                value={option.value}
                aria-label={option.value}
                sx={{
                  minWidth: operationalSizing.compact,
                  height: operationalSizing.compact,
                  px: operationalSpacing.sm,
                  borderRadius: `${operationalRadii.control}px`
                }}
              >
                <Tooltip title={option.tooltip}>
                  <Icon />
                </Tooltip>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      )}

      {/* Ações do header (botões) - no final - apenas no desktop */}
      {!isMobile && headerActions}
    </Box>
  );

  // No layout novo, "fixed" significa fixo dentro do palco central rolável.
  if (fixed) {
    return (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 40,
          mb: 2.5,
          mx: -3,
          px: 3,
          pt: 0.5,
          pb: 1.5,
          background: (theme) => `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper} 72%, rgba(41,42,45,0) 100%)`,
          pointerEvents: 'auto',
        }}
      >
        <Paper
          className={`page-toolbar ${className}`}
          sx={{
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: `${operationalRadii.surface}px`,
            boxShadow: 'none',
            overflow: 'visible',
            '&:hover': {
              boxShadow: 'none'
            }
          }}
          {...props}
        >
          {/* Mobile: Barra compacta retrátil */}
          {isMobile ? (
            <>
              {/* Barra compacta sempre visível */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  gap: 1
                }}
              >
                {/* Título */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {getPageIcon(24)}
                  {title}
                </Typography>

                {/* Contador */}
                {resultCount !== undefined && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap', flex: 1 }}
                  >
                    {resultCount} {resultLabel.split(' ')[0]}
                  </Typography>
                )}

                {/* Botão + Novo Item (sempre visível no mobile) */}
                {onNewItem && (
                  <IconButton
                    size="small"
                    onClick={onNewItem}
                    sx={{
                      bgcolor: theme.palette.mode === 'dark'
                        ? theme.palette.common.white
                        : theme.palette.primary.main,
                      borderRadius: `${operationalRadii.control}px`,
                      boxShadow: operationalElevation.surface,
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark'
                          ? theme.palette.grey[100]
                          : theme.palette.primary.dark,
                        boxShadow: operationalElevation.surface,
                      }
                    }}
                  >
                    <AddIcon
                      sx={{
                        fontSize: '1.2rem',
                        color: theme.palette.mode === 'dark'
                          ? theme.palette.common.black
                          : theme.palette.common.white
                      }}
                    />
                  </IconButton>
                )}

                {/* Botão de expandir/retrair */}
                <IconButton
                  size="small"
                  onClick={() => setMobileToolbarExpanded(!mobileToolbarExpanded)}
                  sx={{
                    bgcolor: chipTokens.background,
                    color: theme.palette.text.primary,
                    borderRadius: `${operationalRadii.control}px`,
                    '&:hover': {
                      bgcolor: chipTokens.backgroundHover
                    }
                  }}
                >
                  {mobileToolbarExpanded ? (
                    <ExpandLessIcon sx={{ fontSize: '1.2rem' }} />
                  ) : (
                    <TuneIcon sx={{ fontSize: '1.2rem' }} />
                  )}
                </IconButton>
              </Box>

              {/* Toolbar completa expansível */}
              <Collapse in={mobileToolbarExpanded}>
                <Box sx={{ p: 1.5, pt: 0, borderTop: `1px solid ${theme.palette.divider}` }}>
                  {renderToolbarContent()}
                </Box>
              </Collapse>
            </>
          ) : (
            /* Desktop: Toolbar completa sempre visível */
            <Box sx={{ p: 1.5 }}>
              {renderToolbarContent()}

              {/* Contador de resultados */}
              {resultCount !== undefined && searchValue && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {resultCount} {resultLabel}
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  // Modo sticky (não fixed)
  return (
    <Paper
      className={`page-toolbar ${className}`}
      sx={{
        position: 'sticky',
        top: 0,
        mb: 3,
        zIndex: 1000,
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: `${operationalRadii.surface}px`,
        boxShadow: 'none',
        overflow: 'visible',
        '&:hover': {
          boxShadow: 'none'
        }
      }}
      {...props}
    >
      {/* Mobile: Barra compacta retrátil */}
      {isMobile ? (
        <>
          {/* Barra compacta sempre visível */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              gap: 1
            }}
          >
            {/* Título */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {getPageIcon(24)}
              {title}
            </Typography>

            {/* Contador */}
            {resultCount !== undefined && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', flex: 1 }}
              >
                {resultCount} {resultLabel.split(' ')[0]}
              </Typography>
            )}

            {/* Botão + Novo Item (sempre visível no mobile) */}
            {onNewItem && (
              <IconButton
                size="small"
                onClick={onNewItem}
                sx={{
                  bgcolor: theme.palette.mode === 'dark'
                    ? theme.palette.common.white
                    : theme.palette.primary.main,
                  borderRadius: `${operationalRadii.control}px`,
                  boxShadow: operationalElevation.surface,
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? theme.palette.grey[100]
                      : theme.palette.primary.dark,
                    boxShadow: operationalElevation.surface,
                  }
                }}
              >
                <AddIcon
                  sx={{
                    fontSize: '1.2rem',
                    color: theme.palette.mode === 'dark'
                      ? theme.palette.common.black
                      : theme.palette.common.white
                  }}
                />
              </IconButton>
            )}

            {/* Botão de expandir/retrair */}
            <IconButton
              size="small"
              onClick={() => setMobileToolbarExpanded(!mobileToolbarExpanded)}
              sx={{
                bgcolor: chipTokens.background,
                color: theme.palette.text.primary,
                borderRadius: `${operationalRadii.control}px`,
                '&:hover': {
                  bgcolor: chipTokens.backgroundHover
                }
              }}
            >
              {mobileToolbarExpanded ? (
                <ExpandLessIcon sx={{ fontSize: '1.2rem' }} />
              ) : (
                <TuneIcon sx={{ fontSize: '1.2rem' }} />
              )}
            </IconButton>
          </Box>

          {/* Toolbar completa expansível */}
          <Collapse in={mobileToolbarExpanded}>
            <Box sx={{ p: 1.5, pt: 0, borderTop: `1px solid ${theme.palette.divider}` }}>
              {renderToolbarContent()}
            </Box>
          </Collapse>
        </>
      ) : (
        /* Desktop: Toolbar completa sempre visível */
        <Box sx={{ p: 1.5 }}>
          {renderToolbarContent()}

          {/* Contador de resultados */}
          {resultCount !== undefined && searchValue && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {resultCount} {resultLabel}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

PageToolbar.propTypes = {
  title: PropTypes.string.isRequired,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  sortValue: PropTypes.string,
  onSortChange: PropTypes.func,
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  viewValue: PropTypes.string,
  onViewChange: PropTypes.func,
  viewOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      tooltip: PropTypes.string.isRequired
    })
  ),
  headerActions: PropTypes.node,
  extraControls: PropTypes.node,
  resultCount: PropTypes.number,
  resultLabel: PropTypes.string,
  fixed: PropTypes.bool,
  className: PropTypes.string,
  // Novas props para mobile
  onNewItem: PropTypes.func,
  newItemLabel: PropTypes.string
};

export default PageToolbar;
